import { IonLabel } from "@ionic/react";
import { useContext, useEffect, useState } from "react";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "../util/AppInsights";
import {
  CriteriaParameter,
  FilterParam,
  QueryResult,
} from "../hooks/useQueryExec";
import { TranslationMessagesContext } from "../util/Translations";
import {
  getUserRequesterAccess,
  getUserTechnicianAccess,
} from "../util/UserHelper";
import WorkOrderCard from "./WorkOrderCard";
import { SelectedWo } from "../models/workorders/WorkOrder";
import { useHistory, useLocation } from "react-router";

interface WorkOrderResultsParams {
  searchYn: boolean;
  setSearchYn: (arg0: boolean) => void;
  hideFilter?: boolean;
  queryName: string;
  queryParams?: FilterParam[];
  criteria?: CriteriaParameter[];
  queryRows: QueryResult[];
  setParameter?: (
    paramKey: string,
    paramValue: string | number | null | undefined
  ) => void;
  executeSearch: (
    queryName: string,
    parameters?: CriteriaParameter[]
  ) => Promise<void>;
  getPriorityCodes?: () => void;
  getStatusCodes?: () => void;
  getNoOfFieldsToDisplay: (option: string) => void;
  errorState: string;
  totalCount: number;
  showLoading: boolean;
  setShowLoading: (arg0: boolean) => void;
  onQueryRowClick?: (row: QueryResult) => void;
  filterRows: QueryResult[];
  filterMethod: (queryString: string) => Promise<void>;
  filterString: string;
  storesRoute?: boolean;
  storesTab?: string;
  isSelectable?: boolean;
  isSelectionLimitReached?: boolean;
  onToggle?: (cardData: SelectedWo) => void;
  noDataMessage?: string;
  selectedWoIds?: string[];
}

const WorkOrderResults: React.FC<WorkOrderResultsParams> = (props) => {
  const {
    searchYn,
    setSearchYn,
    hideFilter,
    queryName,
    queryParams,
    criteria,
    queryRows,
    setParameter,
    executeSearch,
    getPriorityCodes,
    getNoOfFieldsToDisplay,
    errorState,
    totalCount,
    showLoading,
    setShowLoading,
    onQueryRowClick,
    filterRows,
    filterMethod,
    filterString,
    storesRoute,
    storesTab,
    isSelectable = false,
    isSelectionLimitReached = false,
    onToggle,
    noDataMessage,
    selectedWoIds,
  } = props;
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const [searchExecuted, setSearchExecuted] = useState(false);
  const [woLink, setWoLink] = useState("");
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    setShowLoading(true);
    loadData().finally(() => {
      setShowLoading(false);
    });
  }, [searchYn]);

  const loadData = async () => {
    await Promise.all([
      setWorkOrderPath(),
      setQueryParams(),
    ]);
    if (getPriorityCodes) {
      getPriorityCodes();
    }
    await Promise.all([
      getNoOfFieldsToDisplay("NoOfFieldsToDisplay"),
      beginSearch(),
    ]);
  };

  const setQueryParams = async () => {
    if (setParameter && queryParams) {
      queryParams.forEach((p) => {
        setParameter(p.ColumnName, p.ParameterValue);
      });
    }
  };
  async function setWorkOrderPath() {
    if (await getUserTechnicianAccess()) {
      setWoLink("/workorder");
    } else if (await getUserRequesterAccess()) {
      setWoLink("/requester/workorder");
    }
  }
  const beginSearch = async () => {
    if (searchYn) {
      setShowLoading(true);
      await executeSearch(queryName, criteria);
      setSearchYn(false);
      setSearchExecuted(true);
      setShowLoading(false);
    }
  };

  const noResultsFoundMessage =
    noDataMessage ??
    (translatedMessages["NoResultsFound"]?.MessageText || "No results found.");

  return (
    <>
      {errorState ? (
        <div style={{ padding: 8 }} hidden={errorState === ""}>
          <IonLabel color={"danger"}>{errorState}</IonLabel>
        </div>
      ) : (
        <div
          style={{ padding: 8 }}
          hidden={showLoading || !searchExecuted || totalCount > 0}
        >
          <IonLabel color={"warning"}>{noResultsFoundMessage}</IonLabel>
        </div>
      )}

      {!!queryRows && queryRows.length > 0 && (
        <WorkOrderCard
          allRows={queryRows}
          filterString={filterString}
          filteredRows={filterRows}
          onFilter={filterMethod}
          woLink={woLink}
          hideFilter={hideFilter}
          isSelectable={isSelectable}
          isSelectionLimitReached={isSelectionLimitReached}
          onQueryRowClick={onQueryRowClick}
          onToggle={onToggle}
          storesRoute={storesRoute}
          storesTab={storesTab}
          selectedWoIds={selectedWoIds}
        />
      )}
    </>
  );
};

export default withAITracking(
  reactPlugin,
  WorkOrderResults,
  "WorkOrderResults"
);
